export interface SchemaLevel {
  label: string;
  dot: string | null;
  italic?: boolean;
}

export interface FieldDef {
  id: string;
  label: string;
  color: string;
  kw: string[];
  optional?: boolean;
  /** DB table and column this field maps to — used to match against the onboarding schema API response */
  dbTable?: string;
  dbColumn?: string;
  /** If true, this field is excluded from the field-mapper UI */
  hidden?: boolean;
}

export interface FieldGroup {
  label: string;
  hint?: string;
  fields: FieldDef[];
}

export interface ModuleBrief {
  objective: string;
  whatWeNeed: string[];
  outcome: string;
}

export interface ModuleConfig {
  id: string;
  label: string;
  subtitle: string;
  brief: ModuleBrief;
  schema: SchemaLevel[];
  fields: FieldDef[];
  fieldGroups?: FieldGroup[];
  /**
   * Field ids to drop from the mapper entirely — both static fields and columns
   * auto-discovered from the schema API. Use for columns the importer never
   * reads (FKs it resolves itself, server-derived values), which would
   * otherwise show up as mappable and just add noise.
   */
  hiddenFields?: string[];
  steps: string[];
  sumLabels: string[];
  sumColors: string[];
  note?: string;
  successTitle: string;
  successMsg: (counts: number[]) => string;
}

export const MODULE_CONFIG: Record<string, ModuleConfig> = {
  portfolio: {
    id: 'portfolio',
    label: 'Base Hierarchy Configuration',
    subtitle: "Map your data to Kepler's portfolio hierarchy — portfolios, subportfolios, and programs.",
    brief: {
      objective: "Import your organisation's hierarchy — portfolios, subportfolios, and programs — into Kepler.",
      whatWeNeed: ['Portfolio', 'Subportfolio', 'Program'],
      outcome: "Your hierarchy is live in Kepler, ready to link initiatives, risks, and metrics against.",
    },
    schema: [
      { label: 'Portfolio',    dot: '#232459' },
      { label: 'Subportfolio', dot: '#07b797' },
      { label: 'Program',      dot: '#1ac9a8' },
    ],
    fields: [
      { id: 'portfolio',                      label: 'Portfolio',                    color: '#232459', kw: ['portfolio','division','domain','business unit','bu','area'],                   dbTable: 'base_portfoliohierarchy', dbColumn: 'name' },
 { id: 'portfolio_owner_name', label: 'Portfolio Owner', color: '#07b797', kw: ['portfolio owner','owner name','owner'], dbTable: 'base_portfoliohierarchy', dbColumn: 'owner_name' },
 { id: 'portfolio_cost_center_name', label: 'Portfolio Cost Centre Name', color: '#232459', kw: ['portfolio cost centre','cost centre name'], dbTable: 'base_portfoliohierarchy', dbColumn: 'cost_center_name' },
 { id: 'portfolio_cost_center_code', label: 'Portfolio Cost Centre Code', color: '#07b797', kw: ['portfolio cost centre code','cost code'], dbTable: 'base_portfoliohierarchy', dbColumn: 'cost_center_code' },
      { id: 'subportfolio',                   label: 'Subportfolio',                 color: '#07b797', kw: ['subportfolio','sub-portfolio','department','theme','group','function'],        dbTable: 'base_portfoliohierarchy', dbColumn: 'name' },
 { id: 'subportfolio_owner_name', label: 'Subportfolio Owner', color: '#1ac9a8', kw: ['subportfolio owner','sub owner'], dbTable: 'base_portfoliohierarchy', dbColumn: 'owner_name' },
 { id: 'subportfolio_cost_center_name', label: 'Subportfolio Cost Centre Name',color: '#07b797', kw: ['subportfolio cost centre name'], dbTable: 'base_portfoliohierarchy', dbColumn: 'cost_center_name' },
 { id: 'subportfolio_cost_center_code', label: 'Subportfolio Cost Centre Code',color: '#1ac9a8', kw: ['subportfolio cost centre code'], dbTable: 'base_portfoliohierarchy', dbColumn: 'cost_center_code' },
      { id: 'program',                        label: 'Program',                      color: '#1ac9a8', kw: ['program','programme','stream','epic','workstream'],         optional: false, dbTable: 'delivery_program',        dbColumn: 'name' },
 { id: 'program_manager', label: 'Program Manager', color: '#232459', kw: ['program manager','manager'], dbTable: 'delivery_program', dbColumn: 'manager' },
 { id: 'program_description', label: 'Program Description', color: '#07b797', kw: ['program description','program desc'], dbTable: 'delivery_program', dbColumn: 'description' },
 { id: 'program_cost_center_code', label: 'Program Cost Centre Code', color: '#1ac9a8', kw: ['program cost centre code'], dbTable: 'delivery_program', dbColumn: 'cost_center_code' },
 { id: 'program_cost_center_name', label: 'Program Cost Centre Name', color: '#232459', kw: ['program cost centre name'], dbTable: 'delivery_program', dbColumn: 'cost_center_name' },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Portfolios', 'Subportfolios', 'Programs'],
    sumColors: ['#232459', '#07b797', '#1ac9a8'],
    successTitle: 'Base hierarchy activated!',
    successMsg: (c) => `${c[0]} portfolios, ${c[1]} subportfolios, and ${c[2]} programs are now live in Kepler.`,
  },

  initiative: {
    id: 'initiative',
    label: 'Initiative Import',
    subtitle: 'Map your initiatives and link each one to a program in your portfolio hierarchy.',
    brief: {
      objective: 'Import your strategic initiatives and link each one to a program in your base hierarchy.',
      whatWeNeed: ['Initiative', 'Linked Program'],
      outcome: 'Initiatives are live in Kepler, linked to their programs and ready for tracking.',
    },
    schema: [
      { label: 'Program',    dot: '#1ac9a8' },
      { label: 'Initiative', dot: '#1a85d6' },
    ],
    fields: [
      { id: 'initiative',          label: 'Initiative',            color: '#1a85d6', kw: ['initiative','project','project name','name','deliverable','work','item'], dbTable: 'delivery_projectmapping', dbColumn: 'project_name' },
      { id: 'linked_program',      label: 'Linked Program',        color: '#1ac9a8', kw: ['program','programme','linked program','parent program','parent'] },
 { id: 'begin_date', label: 'Start Date', color: '#1a85d6', kw: ['start date','begin date','start','from','begin'], dbTable: 'delivery_projectmapping', dbColumn: 'begin_date' },
 { id: 'expected_end_date', label: 'End Date', color: '#1ac9a8', kw: ['end date','expected end','finish','due date','to'], dbTable: 'delivery_projectmapping', dbColumn: 'expected_end_date' },
 { id: 'project_value', label: 'Project Value', color: '#1a85d6', kw: ['project value','value','budget','cost','investment'], dbTable: 'delivery_projectmapping', dbColumn: 'project_value' },
 { id: 'funding_status', label: 'Funding Status', color: '#1ac9a8', kw: ['funding status','funding','funded','approval status'], dbTable: 'delivery_projectmapping', dbColumn: 'funding_status' },
 { id: 'scope_description', label: 'Scope Description', color: '#1a85d6', kw: ['scope','scope description','scope of work','description'], dbTable: 'delivery_projectmapping', dbColumn: 'scope_description' },
 { id: 'est_benefit', label: 'Estimated Benefit', color: '#1ac9a8', kw: ['estimated benefit','est benefit','benefit','expected benefit'], dbTable: 'delivery_projectmapping', dbColumn: 'est_benefit' },
 { id: 'est_benefit_comments',label: 'Benefit Comments', color: '#1a85d6', kw: ['benefit comments','benefit notes','comments'], dbTable: 'delivery_projectmapping', dbColumn: 'est_benefit_comments' },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Initiatives', 'Programs Linked'],
    sumColors: ['#1a85d6', '#1ac9a8'],
    note: 'Each initiative must be linked to a program from your base hierarchy.',
    successTitle: 'Initiatives activated!',
    successMsg: (c) => `${c[0]} initiatives are now live in Kepler, linked across ${c[1]} programs.`,
  },

  risk: {
    id: 'risk',
    label: 'Risk Import',
    subtitle: 'Map your risk register — risk domains, subdomains, and individual risks.',
    brief: {
      objective: 'Import your risk register into Kepler, structured by domain, subdomain, and individual risk.',
      whatWeNeed: ['Risk Domain', 'Risk Subdomain', 'Risk'],
      outcome: 'Your risk register is live in Kepler, organised by domain and ready for assessment.',
    },
    schema: [
      { label: 'Risk Domain',    dot: '#1a7a6a' },
      { label: 'Risk Subdomain', dot: '#2a9d87' },
      { label: 'Risk',           dot: '#5b9cf6' },
    ],
    fields: [
      { id: 'risk_domain',            label: 'Risk Domain',          color: '#1a7a6a', kw: ['domain','risk domain','category','risk category','area','type'],                                    dbTable: 'delivery_riskdomain',    dbColumn: 'name' },
 { id: 'risk_domain_impact', label: 'Domain Impact', color: '#1a7a6a', kw: ['domain impact','risk domain impact'], dbTable: 'delivery_riskdomain', dbColumn: 'impact' },
 { id: 'risk_domain_prefix', label: 'Domain Prefix', color: '#1a7a6a', kw: ['domain prefix','risk domain prefix'], dbTable: 'delivery_riskdomain', dbColumn: 'prefix' },
      { id: 'risk_subdomain',         label: 'Risk Subdomain',       color: '#2a9d87', kw: ['subdomain','sub-domain','subcategory','sub category','sub-category','risk subdomain'],              dbTable: 'delivery_risksubdomain', dbColumn: 'name' },
 { id: 'risk_subdomain_impact', label: 'Subdomain Impact', color: '#2a9d87', kw: ['subdomain impact','risk subdomain impact'], dbTable: 'delivery_risksubdomain', dbColumn: 'impact' },
 { id: 'risk_subdomain_prefix', label: 'Subdomain Prefix', color: '#2a9d87', kw: ['subdomain prefix','risk subdomain prefix'], dbTable: 'delivery_risksubdomain', dbColumn: 'prefix' },
      { id: 'risk',                   label: 'Risk',                 color: '#5b9cf6', kw: ['risk','risk title','risk name','risk description','title','description','name'],                    dbTable: 'delivery_risk',          dbColumn: 'name' },
 // Impact/likelihood/type are ValueList FKs, not free text — the BE resolves the
 // mapped *word* (e.g. "High") against ValueList and stores the id. dbColumn
 // points at the FK column so the raw `*_id` columns aren't offered separately.
 { id: 'risk_impact', label: 'Risk Impact', color: '#5b9cf6', kw: ['risk impact','impact'], optional: true, dbTable: 'delivery_risk', dbColumn: 'risk_impact_id' },
 { id: 'risk_likelihood', label: 'Risk Likelihood', color: '#5b9cf6', kw: ['risk likelihood','likelihood','probability'], optional: true, dbTable: 'delivery_risk', dbColumn: 'risk_likelihood_id' },
 { id: 'risk_type', label: 'Risk Type', color: '#5b9cf6', kw: ['risk type','type','category'], optional: true, dbTable: 'delivery_risk', dbColumn: 'risk_type_id' },
 { id: 'inherent_risk_score', label: 'Inherent Risk Score', color: '#5b9cf6', kw: ['inherent risk score','inherent score','inherent'], optional: true, dbTable: 'delivery_risk', dbColumn: 'inherent_risk_score' },
 { id: 'residual_risk_impact', label: 'Residual Impact', color: '#5b9cf6', kw: ['residual impact','residual risk impact'], optional: true, dbTable: 'delivery_risk', dbColumn: 'residual_risk_impact_id' },
 { id: 'residual_risk_likelihood', label: 'Residual Likelihood', color: '#5b9cf6', kw: ['residual likelihood','residual risk likelihood'], optional: true, dbTable: 'delivery_risk', dbColumn: 'residual_risk_likelihood_id' },
 { id: 'residual_risk_score', label: 'Residual Risk Score', color: '#5b9cf6', kw: ['residual risk score','residual score','residual'], optional: true, dbTable: 'delivery_risk', dbColumn: 'residual_risk_score' },
 { id: 'risk_prefix', label: 'Risk Prefix', color: '#5b9cf6', kw: ['risk prefix','prefix','risk id'], dbTable: 'delivery_risk', dbColumn: 'prefix' },
 { id: 'risk_short_description', label: 'Risk Short Description',color: '#5b9cf6', kw: ['risk short description','short description','summary'], optional: true, dbTable: 'delivery_risk', dbColumn: 'short_description' },
 // Vulnerabilities live in their own master table (one row per vulnerability,
 // linked to the risk by name like controls/mitigations), not on delivery_risk.
 // Pinned optional so a register can be imported without any vulnerability data.
 { id: 'risk_vulnerability', label: 'Vulnerability', color: '#5b9cf6', kw: ['vulnerability','risk vulnerability','weakness'], optional: true, dbTable: 'delivery_masterriskvulnerability', dbColumn: 'vulnerability' },
 // Controls are NOT NULL in delivery_masterriskcontrol, which would make the
 // schema API mark them required. Pinned optional so a risk register can be
 // imported without any control data.
 // `risk_id` carries the risk *name* — the BE links each control by matching it
 // against Risk.display_name — so its kw mirrors the `risk` field above to make
 // auto-map resolve both to the same column.
 { id: 'control_type', label: 'Control Type', color: '#5b9cf6', kw: ['control type','control'], optional: true, dbTable: 'delivery_masterriskcontrol', dbColumn: 'control_type' },
 { id: 'description', label: 'Control Description', color: '#5b9cf6', kw: ['control description','control desc'], optional: true, dbTable: 'delivery_masterriskcontrol', dbColumn: 'description' },
 { id: 'risk_id', label: 'Linked Risk', color: '#5b9cf6', kw: ['risk','risk title','risk name','risk description','title','description','name'], optional: true, dbTable: 'delivery_masterriskcontrol', dbColumn: 'risk_id' },
 // Mitigations mirror controls: task text is NOT NULL, pinned optional so a
 // register can be imported without any mitigation data.
 { id: 'task', label: 'Mitigation Task', color: '#5b9cf6', kw: ['mitigation','mitigation task','action task','remediation'], optional: true, dbTable: 'delivery_masterriskmitigations', dbColumn: 'task' },
    ],
    // `display_name` and `risk_domain_id` each exist on more than one risk table,
    // so hiding by id clears them from every section at once.
    hiddenFields: [
      // Risk Domain
      'risk_domain_impact', 'risk_domain_prefix', 'display_name', 'order',
      // Risk Subdomain
      'risk_subdomain_impact', 'risk_subdomain_prefix', 'risk_domain_id',
      // Risk
      'tags', 'risk_prefix', 'risk_sub_domain_id',
      // delivery_risk.impact — free-text column the importer never writes
      // (Risk Impact above feeds the ValueList FK instead).
      'impact',
      // Scores are computed by the BE (likelihood x impact from the ValueList
      // weights), so they are not mapped from the sheet.
      'inherent_risk_score', 'residual_risk_score',
      // delivery_masterriskvulnerability.is_ignored — a UI flag, defaults off.
      'is_ignored',
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Risk Domains', 'Risk Subdomains', 'Risks', 'Controls'],
    sumColors: ['#1a7a6a', '#2a9d87', '#5b9cf6', '#a855f7'],
    successTitle: 'Risk register activated!',
    successMsg: (c) => `${c[0]} risk domains, ${c[1]} subdomains, ${c[2]} risks, and ${c[3]} controls are now live in Kepler.`,
  },

  okr: {
    id: 'okr',
    label: 'OKR Import',
    subtitle: 'Map your objectives and key results.',
    brief: {
      objective: 'Import your objectives, key results and goals into Kepler to enable goal tracking.',
      whatWeNeed: ['Objective', 'Key Result', 'Goal'],
      outcome: 'OKRs are live in Kepler.',
    },
    schema: [
      { label: 'Objective',     dot: '#1a7a6a' },
      { label: 'Key Result(s)', dot: '#2a9d87' },
      { label: 'Goals', dot: '#2a9d87' },
    ],
    fields: [
      { id: 'objective',               label: 'Objective',           color: '#1a7a6a', kw: ['objective','strategic objective','okr','aim'] },
      { id: 'key_result',              label: 'Key Result',          color: '#2a9d87', kw: ['key result','kr','result','outcome','key result name'], dbTable: 'opportunity_keyresults', dbColumn: 'title' },
 { id: 'key_result_description', label: 'Key Result Description', color: '#1a7a6a', kw: ['key result description','kr description','kr desc'], dbTable: 'opportunity_keyresults', dbColumn: 'description' },
 { id: 'key_result_end_date', label: 'Key Result End Date', color: '#2a9d87', kw: ['key result end date','kr end date','kr due date'], dbTable: 'opportunity_keyresults', dbColumn: 'end_date' },
      { id: 'goal',                    label: 'Goal',                color: '#2a9d87', kw: ['goal','goals'],                                        dbTable: 'opportunity_keyresultmeasureofsuccess', dbColumn: 'goal_name' },
 { id: 'goal_description', label: 'Goal Description', color: '#1a7a6a', kw: ['goal description','goal desc'], dbTable: 'opportunity_keyresultmeasureofsuccess', dbColumn: 'goal_description' },
 { id: 'goal_end_date', label: 'Goal End Date', color: '#2a9d87', kw: ['goal end date','goal due date'], dbTable: 'opportunity_keyresultmeasureofsuccess', dbColumn: 'end_date' },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Objectives', 'Key Results', 'Goals'],
    sumColors: ['#1a7a6a', '#2a9d87', '#2a9d87'],
    successTitle: 'OKRs activated!',
    successMsg: (c) => `${c[0]} objectives with ${c[1]} key results and ${c[2]} goals are now live in Kepler.`,
  },

  delivery_obligations: {
    id: 'delivery_obligations',
    label: 'Delivery Obligations Import',
    subtitle: 'Map your contractual delivery obligations — categories, subcategories, and terms.',
    brief: {
      objective: 'Import your delivery obligations into Kepler, structured by category, subcategory, and term.',
      whatWeNeed: ['Term Category', 'Term Subcategory', 'Obligation', 'Description'],
      outcome: 'Delivery obligations are live in Kepler, organised by category and ready for tracking.',
    },
    schema: [
      { label: 'Term Category',    dot: '#7c3aed' },
      { label: 'Term Subcategory', dot: '#a78bfa' },
      { label: 'Obligation',       dot: '#c4b5fd' },
    ],
    fields: [
      { id: 'term_category',    label: 'Term Category',    color: '#7c3aed', kw: ['category','term category','type','classification'],                         dbTable: 'term_termcategory',       dbColumn: 'name' },
      { id: 'term_subcategory', label: 'Term Subcategory', color: '#a78bfa', kw: ['subcategory','sub-category','term subcategory','subtype'],                   dbTable: 'term_termsubcategory',    dbColumn: 'name' },
      { id: 'obligation',       label: 'Obligation',       color: '#c4b5fd', kw: ['obligation','delivery obligation','term','sla','kpi','requirement'],         dbTable: 'term_termsofengagement',  dbColumn: 'title' },
 { id: 'description', label: 'Description', color: '#7c3aed', kw: ['description','desc','details','summary'], dbTable: 'term_termsofengagement', dbColumn: 'description' },
 { id: 'definition_of_done', label: 'Definition of Done', color: '#a78bfa', kw: ['definition of done','dod','done criteria','acceptance criteria','completion criteria'], dbTable: 'term_termsofengagement', dbColumn: 'defination_of_done' },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Categories', 'Subcategories', 'Obligations'],
    sumColors: ['#7c3aed', '#a78bfa', '#c4b5fd'],
    successTitle: 'Delivery obligations activated!',
    successMsg: (c) => `${c[2]} delivery obligations across ${c[0]} categories are now live in Kepler.`,
  },

  metrics: {
    id: 'metrics',
    label: 'Metrics Import',
    subtitle: 'Map your master metrics list for OKRs, balanced scorecard, and delivery obligations.',
    brief: {
      objective: 'Load your master metrics catalogue into Kepler to enable measurement across OKRs and scorecards.',
      whatWeNeed: ['Metric Name', 'Description', 'Unit', 'Function', 'Formula', 'Grouping'],
      outcome: 'Your metrics library is live and available in Kepler.',
    },
    schema: [
      { label: 'Metric',    dot: '#0891b2' },
      { label: 'Indicator', dot: '#22d3ee' },
    ],
    fields: [
      { id: 'metric_name',  label: 'Metric Name',  color: '#0891b2', kw: ['metric','metric name','name','kpi','measure'],                             dbTable: 'ipdb_metrics_instancemetrics', dbColumn: 'name' },
 { id: 'description', label: 'Description', color: '#22d3ee', kw: ['description','definition','detail','desc'], dbTable: 'ipdb_metrics_instancemetrics', dbColumn: 'description' },
 { id: 'unit', label: 'Unit', color: '#0891b2', kw: ['unit','unit of measure','uom','measure type'], dbTable: 'ipdb_metrics_instancemetrics', dbColumn: 'metrics_unit_id' },
 { id: 'function', label: 'Function', color: '#22d3ee', kw: ['function','metric function','category','domain'], dbTable: 'ipdb_metrics_instancemetrics', dbColumn: 'function_id' },
 { id: 'formula', label: 'Formula', color: '#0891b2', kw: ['formula','calculation','mos formula','method'], dbTable: 'ipdb_metrics_instancemetrics', dbColumn: 'metrics_calculation_id' },
 { id: 'grouping', label: 'Grouping', color: '#22d3ee', kw: ['grouping','group','metric group','cluster'], dbTable: 'ipdb_metrics_instancemetrics', dbColumn: 'metrics_grouping_id' },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Metrics'],
    sumColors: ['#0891b2'],
    successTitle: 'Metrics activated!',
    successMsg: (c) => `${c[0]} metrics are now live in Kepler.`,
  },

  vendor_master: {
    id: 'vendor_master',
    label: 'Vendor Master Import',
    subtitle: 'Map your vendor registry — names, contacts, and founding details.',
    brief: {
      objective: "Populate Kepler's vendor registry with your supplier and partner organisations.",
      whatWeNeed: ['Name', 'Primary Contact Name', 'Primary Contact Email', 'Domain Name', 'Short Name', 'Year Founded', 'Year of Incorporation', 'Category'],
      outcome: 'All vendors are registered in Kepler.',
    },
    schema: [
      { label: 'Vendor', dot: '#b45309' },
    ],
    fields: [
      { id: 'name',                  label: 'Name',                  color: '#b45309', kw: ['vendor','vendor name','name','supplier','partner'],                       dbTable: 'opportunity_vendor', dbColumn: 'name' },
      { id: 'primary_contact_name',  label: 'Primary Contact Name',  color: '#d97706', kw: ['primary contact','contact name','contact','poc'],                          dbTable: 'opportunity_vendor', dbColumn: 'primary_contact_name' },
      { id: 'primary_contact_email', label: 'Primary Contact Email', color: '#b45309', kw: ['primary contact email','contact email','email'],                           dbTable: 'opportunity_vendor', dbColumn: 'primary_contact_email' },
      { id: 'domain_name',           label: 'Domain Name',           color: '#d97706', kw: ['domain','domain name','website domain'],                                   dbTable: 'opportunity_vendor', dbColumn: 'domain_name' },
      { id: 'short_name',            label: 'Short Name',            color: '#b45309', kw: ['short name','shortname','abbreviation','short'],                           dbTable: 'opportunity_vendor', dbColumn: 'short_name' },
      { id: 'year',                  label: 'Year Founded',          color: '#d97706', kw: ['year founded','founded','year','established'],                              dbTable: 'opportunity_vendor', dbColumn: 'year' },
      { id: 'year_of_incorp',        label: 'Year of Incorporation', color: '#b45309', kw: ['year of incorp','year of incorporation','incorporation year','incorp'],      dbTable: 'opportunity_vendor', dbColumn: 'year_of_incorp' },
      { id: 'category', label: 'Category', color: '#d97706', kw: ['category','vendor category','type','classification'], dbTable: 'opportunity_vendor', dbColumn: 'category' },
      { id: 'is_gdg',        label: 'Is GDG', color: '#b45309', kw: ['is gdg','gdg'], optional: true, dbTable: 'opportunity_vendor', dbColumn: 'is_gdg' },

    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Vendors'],
    sumColors: ['#b45309'],
    successTitle: 'Vendor master activated!',
    successMsg: (c) => `${c[0]} vendors are now live in Kepler.`,
  },

  tech_map_applications: {
    id: 'tech_map_applications',
    label: 'Applications',
    subtitle: 'Map your application inventory into Kepler\'s Tech Map.',
    brief: {
      objective: "Catalogue your application inventory in Kepler's Tech Map so it can be linked to infrastructure components and services.",
      whatWeNeed: ['Application Name', 'Description', 'Data Quality Score', 'System Code', 'Business Area', 'Physical Location'],
      outcome: "Applications are registered in Kepler's Tech Map and ready to be linked to infrastructure and services.",
    },
    schema: [
      { label: 'Application', dot: '#0f766e' },
    ],
    fields: [
      { id: 'name',              label: 'Application Name',   color: '#0f766e', kw: ['application','application name','name','system','app'], optional: false,                        dbTable: 'delivery_applicationmaster', dbColumn: 'name' },
      { id: 'description',       label: 'Description',        color: '#14b8a6', kw: ['description','desc','details','summary'], optional: false,                                        dbTable: 'delivery_applicationmaster', dbColumn: 'description' },
      { id: 'data_quality_score', label: 'Data Quality Score', color: '#0f766e', kw: ['data quality','quality score','dq score','score'], optional: false,                               dbTable: 'delivery_applicationmaster', dbColumn: 'data_quality_score' },
 { id: 'sys_code', label: 'System Code', color: '#14b8a6', kw: ['sys code','system code','code','app code'], dbTable: 'delivery_applicationmaster', dbColumn: 'sys_code' },
 { id: 'business_area', label: 'Business Area', color: '#0f766e', kw: ['business area','area','domain','business unit','bu'], dbTable: 'delivery_applicationmaster', dbColumn: 'business_area' },
 { id: 'physical_location', label: 'Physical Location', color: '#14b8a6', kw: ['physical location','location','site','data centre'], dbTable: 'delivery_applicationmaster', dbColumn: 'physical_location' },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Applications'],
    sumColors: ['#0f766e'],
    successTitle: 'Applications activated!',
    successMsg: (c) => `${c[0]} applications are now live in Kepler's Tech Map.`,
  },

  tech_map_infrastructures: {
    id: 'tech_map_infrastructures',
    label: 'Infrastructures',
    subtitle: 'Map your infrastructure inventory into Kepler\'s Tech Map.',
    brief: {
      objective: "Import your infrastructure inventory into Kepler's Tech Map to build a complete picture of your technology estate.",
      whatWeNeed: ['Infrastructure Name', 'Infrastructure Type', 'Status', 'Infrastructure ID', 'Description', 'Environment', 'Region', 'Tier'],
      outcome: "Infrastructure components are live in Kepler's Tech Map and can be mapped to applications and services.",
    },
    schema: [
      { label: 'Infrastructure', dot: '#0f766e' },
    ],
    fields: [
      { id: 'infrastructure_name', label: 'Infrastructure Name', color: '#0f766e', kw: ['infrastructure name','name','infrastructure','server','host','resource'], optional: false,    dbTable: 'master_infrastructure', dbColumn: 'infrastructure_name' },
      { id: 'infrastructure_type', label: 'Infrastructure Type', color: '#14b8a6', kw: ['infrastructure type','type','resource type','service type'],                  dbTable: 'master_infrastructure', dbColumn: 'infrastructure_type_id' },
      { id: 'status',              label: 'Status',              color: '#0f766e', kw: ['status','state','active','lifecycle'],                                         dbTable: 'master_infrastructure', dbColumn: 'status_id' },
 { id: 'infrastructure_id', label: 'Infrastructure ID', color: '#14b8a6', kw: ['infrastructure id','id','resource id','identifier'], dbTable: 'master_infrastructure', dbColumn: 'infrastructure_id' },
 { id: 'description', label: 'Description', color: '#0f766e', kw: ['description','desc','details','summary'], optional: false, dbTable: 'master_infrastructure', dbColumn: 'description' },
 { id: 'environment', label: 'Environment', color: '#14b8a6', kw: ['environment','env','stage','deployment'], dbTable: 'master_infrastructure', dbColumn: 'environment' },
 { id: 'region', label: 'Region', color: '#0f766e', kw: ['region','zone','location','aws region'], dbTable: 'master_infrastructure', dbColumn: 'region' },
 { id: 'tier', label: 'Tier', color: '#14b8a6', kw: ['tier','disaster recovery tier','dr tier'], dbTable: 'master_infrastructure', dbColumn: 'tier' },
 // ValueList FKs — the BE resolves `category`/`subcategory` by name, so pin the ids
 // instead of letting schema discovery expose them as `category_id`/`subcategory_id`.
 { id: 'category', label: 'Category', color: '#0f766e', kw: ['category','classification'], dbTable: 'master_infrastructure', dbColumn: 'category_id' },
 { id: 'subcategory', label: 'Subcategory', color: '#14b8a6', kw: ['subcategory','sub category','sub-category'], dbTable: 'master_infrastructure', dbColumn: 'subcategory_id' },
 { id: 'is_scanned', label: 'Is Scanned', color: '#14b8a6', kw: ['is scanned','scanned'], optional:true, dbTable: 'master_infrastructure', dbColumn: 'is_scanned' },

    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Infrastructures'],
    sumColors: ['#0f766e'],
    successTitle: 'Infrastructures activated!',
    successMsg: (c) => `${c[0]} infrastructures are now live in Kepler's Tech Map.`,
  },

  tech_map_services: {
    id: 'tech_map_services',
    label: 'Services',
    subtitle: 'Map your services inventory into Kepler\'s Tech Map.',
    brief: {
      objective: "Register your services in Kepler's Tech Map to document what capabilities your technology estate delivers.",
      whatWeNeed: ['Service Name', 'Service Type', 'Service ID', 'Description', 'Business Area', 'Environment', 'Category', 'Subcategory'],
      outcome: "Services are live in Kepler's Tech Map and ready to be linked to infrastructure and applications.",
    },
    schema: [
      { label: 'Service', dot: '#0f766e' },
    ],
    fields: [
      { id: 'service_name',  label: 'Service Name',  color: '#0f766e', kw: ['service name','name','service','title'], optional: false,                                        dbTable: 'master_service', dbColumn: 'service_name' },
      { id: 'service_type',  label: 'Service Type',  color: '#14b8a6', kw: ['service type','type','category type'],                                              dbTable: 'master_service', dbColumn: 'service_type_id' },
 { id: 'service_id', label: 'Service ID', color: '#0f766e', kw: ['service id','id','identifier'], dbTable: 'master_service', dbColumn: 'service_id' },
 { id: 'description', label: 'Description', color: '#14b8a6', kw: ['description','desc','details','summary'], optional: false, dbTable: 'master_service', dbColumn: 'description' },
 { id: 'business_area', label: 'Business Area', color: '#0f766e', kw: ['business area','area','domain','business unit'], dbTable: 'master_service', dbColumn: 'business_area' },
 { id: 'environment', label: 'Environment', color: '#14b8a6', kw: ['environment','env','stage'], dbTable: 'master_service', dbColumn: 'environment' },
 { id: 'category', label: 'Category', color: '#0f766e', kw: ['category','classification'], dbTable: 'master_service', dbColumn: 'category' },
 { id: 'subcategory', label: 'Subcategory', color: '#14b8a6', kw: ['subcategory','sub category','sub-category'], dbTable: 'master_service', dbColumn: 'subcategory' },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Services'],
    sumColors: ['#0f766e'],
    successTitle: 'Services activated!',
    successMsg: (c) => `${c[0]} services are now live in Kepler's Tech Map.`,
  },

  workforce_catalog: {
    id: 'workforce_catalog',
    label: 'Workforce Catalog Import',
    subtitle: 'Map your workforce data — resources, roles, and project assignments.',
    brief: {
      objective: 'Import your workforce into Kepler, linked to their Kepler project via project ID.',
      whatWeNeed: ['Project ID', 'Resource Name', 'Resource ID', 'Month', 'Year', 'Job Title', 'Job Category', 'Classification', 'Company Name', 'Contract Start Date', 'Contract End Date', 'Employee Type'],
      outcome: "Resources are registered in Kepler's Workforce Catalog with hierarchy auto-resolved from the project ID.",
    },
    schema: [
      { label: 'Project', dot: '#be185d' },
      { label: 'Resource', dot: '#ec4899' },
    ],
    fields: [
      { id: 'project_id',          label: 'Project ID',           color: '#be185d', kw: ['project id','project number','kepler project id','project'], optional: false,                                  dbTable: 'delivery_workforcedashboard', dbColumn: 'project_id' },
      { id: 'resource_name',       label: 'Resource Name',        color: '#ec4899', kw: ['resource name','name','employee','worker','resource'], optional: false,                        dbTable: 'delivery_workforcedashboard', dbColumn: 'resource_name' },
      { id: 'resource_id',         label: 'Resource ID',          color: '#be185d', kw: ['resource id','employee id','staff id','id'], optional: false,                                  dbTable: 'delivery_workforcedashboard', dbColumn: 'resource_id' },
      { id: 'month',               label: 'Month',                color: '#ec4899', kw: ['month','reporting month'],                                                    dbTable: 'delivery_workforcedashboard', dbColumn: 'month' },
      { id: 'year',                label: 'Year',                 color: '#be185d', kw: ['year','reporting year'],                                                      dbTable: 'delivery_workforcedashboard', dbColumn: 'year' },
 { id: 'job_title', label: 'Job Title', color: '#ec4899', kw: ['job title','title','role','position','designation'], optional: false, dbTable: 'delivery_workforcedashboard', dbColumn: 'job_title' },
 { id: 'job_category', label: 'Job Category', color: '#be185d', kw: ['job category','category','job type category'], dbTable: 'delivery_workforcedashboard', dbColumn: 'job_category' },
 { id: 'classification', label: 'Classification', color: '#ec4899', kw: ['classification','resource classification','grade'], dbTable: 'delivery_workforcedashboard', dbColumn: 'classification' },
 { id: 'company_name', label: 'Company / Vendor', color: '#be185d', kw: ['company','vendor','company name','supplier','employer'], dbTable: 'delivery_workforcedashboard', dbColumn: 'company_name' },
 { id: 'contract_start_date', label: 'Contract Start Date', color: '#ec4899', kw: ['contract start','start date','from','contract start date'], dbTable: 'delivery_workforcedashboard', dbColumn: 'contract_start_date' },
 { id: 'contract_end_date', label: 'Contract End Date', color: '#be185d', kw: ['contract end','end date','to','contract end date'], dbTable: 'delivery_workforcedashboard', dbColumn: 'contract_end_date' },
 { id: 'employee_type', label: 'Employee Type', color: '#ec4899', kw: ['employee type','type','worker type','contingent','permanent'], dbTable: 'delivery_workforcedashboard', dbColumn: 'employee_type' },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Resources'],
    sumColors: ['#be185d'],
    successTitle: 'Workforce Catalog activated!',
    successMsg: (c) => `${c[0]} resources are now live in Kepler's Workforce Catalog.`,
  },

  people_competency_map: {
    id: 'people_competency_map',
    label: 'People Competency Map',
    subtitle: 'Import your people competency framework — roles, qualifications, and responsibilities.',
    brief: {
      objective: 'Load your people competency catalogue into Kepler to map roles, skills, and responsibilities.',
      whatWeNeed: ['Title', 'Job Category', 'Client Location', 'Qualification', 'Responsibilities', 'Personas', 'External ID'],
      outcome: 'People competency data is live in Kepler and available for workforce planning.',
    },
    schema: [
      { label: 'Competency', dot: '#7c3aed' },
    ],
    fields: [
      { id: 'title',           label: 'Title',           color: '#7c3aed', kw: ['title','role','job title','competency','position'], optional: false,                             dbTable: 'ipdb_metrics_peoplecompetencymap', dbColumn: 'title' },
 { id: 'job_category', label: 'Job Category', color: '#a78bfa', kw: ['job category','category','job type','function'], optional: false, dbTable: 'ipdb_metrics_peoplecompetencymap', dbColumn: 'job_category' },
 { id: 'client_location', label: 'Client Location', color: '#7c3aed', kw: ['client location','location','site','office'], dbTable: 'ipdb_metrics_peoplecompetencymap', dbColumn: 'client_location' },
 { id: 'qualification', label: 'Qualification', color: '#a78bfa', kw: ['qualification','qualifications','education','degree','cert'], dbTable: 'ipdb_metrics_peoplecompetencymap', dbColumn: 'qualification' },
 { id: 'responsibilities', label: 'Responsibilities', color: '#7c3aed', kw: ['responsibilities','duties','role description'], dbTable: 'ipdb_metrics_peoplecompetencymap', dbColumn: 'responsibilities' },
 { id: 'personas', label: 'Personas', color: '#a78bfa', kw: ['personas','persona','profile','archetype'], dbTable: 'ipdb_metrics_peoplecompetencymap', dbColumn: 'personas' },
 { id: 'external_id', label: 'External ID', color: '#7c3aed', kw: ['external id','ext id','id','reference id','code'], dbTable: 'ipdb_metrics_peoplecompetencymap', dbColumn: 'external_id' },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Competency Entries'],
    sumColors: ['#7c3aed'],
    successTitle: 'People Competency Map activated!',
    successMsg: (c) => `${c[0]} competency entries are now live in Kepler.`,
  },

  tbm_cost_pool: {
    id: 'tbm_cost_pool',
    label: 'Cost Pool Taxonomy',
    subtitle: 'Map your cost pool taxonomy — cost pools, sub-pools, and expense types.',
    brief: {
      objective: 'Load your TBM cost pool taxonomy into Kepler to classify IT spend by pool, sub-pool, and expense type.',
      whatWeNeed: ['Cost Pool', 'Cost Sub-Pool', 'Expense Type', 'Description'],
      outcome: 'Cost pool taxonomy is live in Kepler and available for TBM reporting.',
    },
    schema: [
      { label: 'Cost Pool',     dot: '#b45309' },
      { label: 'Cost Sub-Pool', dot: '#d97706' },
      { label: 'Expense Type',  dot: '#fbbf24' },
    ],
    fields: [
      { id: 'cost_pool',     label: 'Cost Pool',     color: '#b45309', kw: ['cost pool','pool','cost head','cost category'], optional: false,                                 dbTable: 'ipdb_metrics_costpooltaxonomy', dbColumn: 'cost_pool' },
 { id: 'cost_sub_pool', label: 'Cost Sub-Pool', color: '#d97706', kw: ['cost sub pool','sub pool','sub-pool','cost type'], optional: false, dbTable: 'ipdb_metrics_costpooltaxonomy', dbColumn: 'cost_sub_pool' },
 { id: 'expense_type', label: 'Expense Type', color: '#fbbf24', kw: ['expense type','expense','type','cost element'], optional:false, dbTable: 'ipdb_metrics_costpooltaxonomy', dbColumn: 'expense_type' },
 { id: 'description', label: 'Description', color: '#b45309', kw: ['description','desc','details','summary'], dbTable: 'ipdb_metrics_costpooltaxonomy', dbColumn: 'description' },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Cost Pools'],
    sumColors: ['#b45309'],
    successTitle: 'Cost Pool Taxonomy activated!',
    successMsg: (c) => `${c[0]} cost pool entries are now live in Kepler.`,
  },

  tbm_it_tower: {
    id: 'tbm_it_tower',
    label: 'IT Tower Taxonomy',
    subtitle: 'Map your IT tower taxonomy — resource towers, sub-towers, and tower elements.',
    brief: {
      objective: 'Load your IT Tower taxonomy into Kepler to classify IT resources by tower and sub-tower.',
      whatWeNeed: ['IT Resource Tower', 'IT Resource Sub-Tower', 'IT Sub-Tower Element'],
      outcome: 'IT Tower taxonomy is live in Kepler and ready for TBM allocation.',
    },
    schema: [
      { label: 'IT Resource Tower',     dot: '#0f766e' },
      { label: 'IT Resource Sub-Tower', dot: '#14b8a6' },
      { label: 'IT Sub-Tower Element',  dot: '#5eead4' },
    ],
    fields: [
      { id: 'it_resource_tower_name',     label: 'IT Resource Tower',     color: '#0f766e', kw: ['it resource tower','tower','it tower','resource tower'], optional: false,                   dbTable: 'ipdb_metrics_ittowertaxonomy', dbColumn: 'it_resource_tower_name' },
 { id: 'it_resource_sub_tower_name', label: 'IT Resource Sub-Tower', color: '#14b8a6', kw: ['it resource sub tower','sub tower','sub-tower'], optional: false, dbTable: 'ipdb_metrics_ittowertaxonomy', dbColumn: 'it_resource_sub_tower_name' },
 { id: 'delivery', label: 'Delivery', color: '#0f766e', kw: ['delivery','delivery type','delivery method'], dbTable: 'ipdb_metrics_ittowertaxonomy', dbColumn: 'delivery' },
 { id: 'it_sub_tower_element', label: 'IT Sub-Tower Element', color: '#5eead4', kw: ['it sub tower element','sub tower element','element'], optional: false, dbTable: 'ipdb_metrics_ittowertaxonomy', dbColumn: 'it_sub_tower_element' },
 { id: 'description', label: 'Description', color: '#14b8a6', kw: ['description','desc','details','summary'], dbTable: 'ipdb_metrics_ittowertaxonomy', dbColumn: 'description' },
 { id: 'unit_of_measure', label: 'Unit of Measure', color: '#0f766e', kw: ['unit of measure','unit','uom','measure'], dbTable: 'ipdb_metrics_ittowertaxonomy', dbColumn: 'unit_of_measure' },
 { id: 'purpose', label: 'Purpose', color: '#5eead4', kw: ['purpose','objective','goal','intent'], dbTable: 'ipdb_metrics_ittowertaxonomy', dbColumn: 'purpose' },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['IT Tower Entries'],
    sumColors: ['#0f766e'],
    successTitle: 'IT Tower Taxonomy activated!',
    successMsg: (c) => `${c[0]} IT tower entries are now live in Kepler.`,
  },

  tbm_service: {
    id: 'tbm_service',
    label: 'Service Taxonomy',
    subtitle: 'Map your service taxonomy — service types, categories, and definitions.',
    brief: {
      objective: 'Load your TBM service taxonomy into Kepler to catalogue IT services by type and category.',
      whatWeNeed: ['Service Name', 'Service Type', 'Service Category', 'Service Definition', 'Sample Offerings'],
      outcome: 'Service taxonomy is live in Kepler and available for IT service reporting.',
    },
    schema: [
      { label: 'Service Type',     dot: '#7c3aed' },
      { label: 'Service Category', dot: '#a78bfa' },
      { label: 'Service',          dot: '#c4b5fd' },
    ],
    fields: [
      { id: 'service_name',             label: 'Service Name',              color: '#7c3aed', kw: ['service name','name','service','title'], optional: false,                                   dbTable: 'ipdb_metrics_servicetaxonomy', dbColumn: 'service_name' },
 { id: 'service_type', label: 'Service Type', color: '#a78bfa', kw: ['service type','type','service classification'], optional: false, dbTable: 'ipdb_metrics_servicetaxonomy', dbColumn: 'service_type' },
 { id: 'service_category', label: 'Service Category', color: '#c4b5fd', kw: ['service category','category','service group'], optional: false, dbTable: 'ipdb_metrics_servicetaxonomy', dbColumn: 'service_category' },
 { id: 'service_definition', label: 'Service Definition', color: '#7c3aed', kw: ['service definition','definition','description'], dbTable: 'ipdb_metrics_servicetaxonomy', dbColumn: 'service_definition' },
 { id: 'sample_service_offerings', label: 'Sample Service Offerings', color: '#a78bfa', kw: ['sample service offerings','offerings','examples'], dbTable: 'ipdb_metrics_servicetaxonomy', dbColumn: 'sample_service_offerings' },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Services'],
    sumColors: ['#7c3aed'],
    successTitle: 'Service Taxonomy activated!',
    successMsg: (c) => `${c[0]} service entries are now live in Kepler.`,
  },

  tbm_solution: {
    id: 'tbm_solution',
    label: 'Solution Taxonomy',
    subtitle: 'Map your solution taxonomy — solution types, categories, and offerings.',
    brief: {
      objective: 'Load your TBM solution taxonomy into Kepler to catalogue technology solutions by type and category.',
      whatWeNeed: ['Name', 'Solution Type', 'Category', 'Offering'],
      outcome: 'Solution taxonomy is live in Kepler and ready for TBM reporting.',
    },
    schema: [
      { label: 'Solution Type', dot: '#1a7a6a' },
      { label: 'Category',      dot: '#2a9d87' },
      { label: 'Solution',      dot: '#5eead4' },
    ],
    fields: [
      { id: 'name',          label: 'Name',          color: '#1a7a6a', kw: ['name','solution name','solution','title'], optional: false,                                          dbTable: 'ipdb_metrics_solutiontaxonomy', dbColumn: 'name' },
 { id: 'solution_type', label: 'Solution Type', color: '#2a9d87', kw: ['solution type','type','solution category','classification'], optional: false, dbTable: 'ipdb_metrics_solutiontaxonomy', dbColumn: 'solution_type' },
 { id: 'category', label: 'Category', color: '#1a7a6a', kw: ['category','group','solution group'], optional: false, dbTable: 'ipdb_metrics_solutiontaxonomy', dbColumn: 'category' },
 { id: 'offering', label: 'Offering', color: '#5eead4', kw: ['offering','offerings','product','service offering'], dbTable: 'ipdb_metrics_solutiontaxonomy', dbColumn: 'offering' },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Solutions'],
    sumColors: ['#1a7a6a'],
    successTitle: 'Solution Taxonomy activated!',
    successMsg: (c) => `${c[0]} solution entries are now live in Kepler.`,
  },

  turnkey_plans: {
    id: 'turnkey_plans',
    label: 'Plans Import',
    subtitle: "Map and import your turnkey plans — each plan is scaffolded from its template.",
    brief: {
      objective: "Import plans into Kepler. Each plan is automatically structured with phases and activities from its assigned template.",
      whatWeNeed: ['Plan Name'],
      outcome: "Plans are live in Kepler, fully structured with phases and activities and ready to track.",
    },
    schema: [
      { label: 'Plan',     dot: '#0d9488' },
      { label: 'Phase',    dot: '#14b8a6' },
      { label: 'Activity', dot: '#5eead4' },
    ],
    fields: [
      { id: 'name',          label: 'Plan Name',     color: '#0d9488', kw: ['plan name','name','title','plan'],             dbTable: 'turnkey_tracker_plan', dbColumn: 'name'        },
      { id: 'template_name', label: 'Template Name', color: '#14b8a6', kw: ['template','template name','plan template'],   optional: true                                           },
      { id: 'start_date',    label: 'Start Date',    color: '#0d9488', kw: ['start date','start','begin','from'],           dbTable: 'turnkey_tracker_plan', dbColumn: 'start_date'  },
      { id: 'end_date',      label: 'End Date',      color: '#14b8a6', kw: ['end date','end','finish','to'],                dbTable: 'turnkey_tracker_plan', dbColumn: 'end_date'    },
      { id: 'description',   label: 'Description',   color: '#0d9488', kw: ['description','desc','summary','details'],      dbTable: 'turnkey_tracker_plan', dbColumn: 'description' },
      { id: 'status',        label: 'Status',        color: '#14b8a6', kw: ['status','state','plan status'],                dbTable: 'turnkey_tracker_plan', dbColumn: 'status'      },
      { id: 'origin',        label: 'Origin',        color: '#0d9488', kw: ['origin','source'],                             dbTable: 'turnkey_tracker_plan', dbColumn: 'origin'      },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Plans'],
    sumColors: ['#0d9488'],
    successTitle: 'Plans activated!',
    successMsg: (c) => `${c[0]} plans are now live in Kepler.`,
  },

  expert_led: {
    id: 'expert_led',
    label: 'Expert Led',
    subtitle: '',
    brief: {
      objective: 'This section will guide you through an expert-led onboarding process.',
      whatWeNeed: [],
      outcome: '',
    },
    schema: [],
    fields: [],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: [],
    sumColors: [],
    successTitle: '',
    successMsg: () => '',
  },

  profit_cost_centres: {
    id: 'profit_cost_centres',
    label: 'Profit-Cost Centres Import',
    subtitle: 'Map your profit and cost centres — codes, descriptions, owners, and types.',
    brief: {
      objective: 'Configure your profit and cost centre structure in Kepler.',
      whatWeNeed: ['Code', 'Description', 'Owner Email', 'Type'],
      outcome: 'Profit and cost centres are live in Kepler.',
    },
    schema: [
      { label: 'Profit/Cost Centre', dot: '#15803d' },
    ],
    fields: [
      { id: 'code',        label: 'Code',        color: '#15803d', kw: ['code','centre code','cost code','profit code','id'], dbTable: 'master_profitcostcenter', dbColumn: 'code' },
      { id: 'description', label: 'Description', color: '#22c55e', kw: ['description','desc','name','details'], dbTable: 'master_profitcostcenter', dbColumn: 'description' },
      { id: 'owner_email', label: 'Owner Email', color: '#15803d', kw: ['owner email','owner','email','owner e-mail'], optional: false },
      { id: 'type',        label: 'Type',        color: '#22c55e', kw: ['type','centre type','cost centre','profit centre','classification'], optional: false },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Centres'],
    sumColors: ['#15803d'],
    successTitle: 'Profit-Cost Centres activated!',
    successMsg: (c) => `${c[0]} profit/cost centres are now live in Kepler.`,
  },

  module_owners: {
    id: 'module_owners',
    label: 'Zone Owners',
    subtitle: 'Map owners to each zone in Kepler — one row per zone, one owner per zone.',
    brief: {
      objective: 'Assign or update the owner for each zone in Kepler.',
      whatWeNeed: ['Zone Name', 'Owner Name'],
      outcome: 'Each zone in Kepler has an owner assigned, visible across the platform.',
    },
    schema: [
      { label: 'Zone',  dot: '#7c3aed' },
      { label: 'Owner', dot: '#a78bfa' },
    ],
    fields: [
      {
        id: 'zone_name',
        label: 'Zone Name',
        color: '#7c3aed',
        kw: ['zone', 'zone name', 'area', 'region', 'sector'],
        // TODO: set dbTable and dbColumn once the backend table is confirmed
        // dbTable: 'base_zone',
        // dbColumn: 'name',
      },
      {
        id: 'owner_name',
        label: 'Owner Name',
        color: '#a78bfa',
        kw: ['owner', 'owner name', 'assignee', 'responsible', 'person'],
        // The spreadsheet carries the owner's full name. The API resolves it
        // to an owner_id before writing to the DB.
        // TODO: set dbTable and dbColumn once the backend table is confirmed
        // dbTable: 'base_zone',
        // dbColumn: 'owner_id',
      },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Zone Assignments'],
    sumColors: ['#7c3aed'],
    successTitle: 'Zone owners activated!',
    successMsg: (c) => `${c[0]} zone owner ${c[0] === 1 ? 'assignment has' : 'assignments have'} been applied in Kepler.`,
  },

  catalog_owners: {
    id: 'catalog_owners',
    label: 'Catalog Owners',
    subtitle: 'Assign owners to catalog entities — e.g. link a Risk Register domain to its owner.',
    brief: {
      objective: 'Assign or update the owner for entities in your Kepler catalog, such as Risk Register domains.',
      whatWeNeed: ['Catalog Key', 'Subkey', 'Owner Name'],
      outcome: 'Each catalog entity has an owner assigned, visible across the platform.',
    },
    schema: [
      { label: 'Catalog Key', dot: '#0891b2' },
      { label: 'Subkey',      dot: '#22d3ee' },
      { label: 'Owner',       dot: '#0e7490' },
    ],
    fields: [
      {
        id: 'catalog_key',
        label: 'Catalog Key',
        color: '#0891b2',
        kw: ['catalog key', 'catalog', 'key', 'register'],
        dbTable: 'users_catalogpermissions', dbColumn: 'catalog_key',
        // Value is matched server-side against a catalog_key -> content type mapping (e.g. "Risk Register" -> riskdomain) to resolve entity_type_id.
      },
      {
        id: 'subkey',
        label: 'Subkey',
        color: '#22d3ee',
        kw: ['subkey', 'sub key', 'domain', 'risk domain', 'entity'],
        dbTable: 'users_catalogpermissions', dbColumn: 'subkey',
        // Value is resolved server-side to the matching entity's id (e.g. the risk domain's id) before being stored here.
      },
      {
        id: 'owner_name',
        label: 'Owner Name',
        color: '#0e7490',
        kw: ['owner name', 'owner', 'assignee', 'responsible', 'person'],
        // Resolved server-side against auth_user's "first_name last_name" to owner_id — not a direct column.
      },
      // Hidden — these DB columns require server-side resolution (see catalog_key/owner_name
      // above) rather than being mapped directly from the spreadsheet. Declaring them here
      // (with hidden: true) stops the schema service from offering them as raw mappable fields.
      {
        id: '_catalog_entity_type_id',
        label: 'Entity Type Id',
        color: '#0891b2',
        kw: [],
        dbTable: 'users_catalogpermissions', dbColumn: 'entity_type_id',
        hidden: true,
      },
      {
        id: '_catalog_owner_id',
        label: 'Owner Id',
        color: '#0e7490',
        kw: [],
        dbTable: 'users_catalogpermissions', dbColumn: 'owner_id',
        hidden: true,
      },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Catalog Owner Assignments'],
    sumColors: ['#0891b2'],
    successTitle: 'Catalog owners activated!',
    successMsg: (c) => `${c[0]} catalog owner ${c[0] === 1 ? 'assignment has' : 'assignments have'} been applied in Kepler.`,
  },

  audits: {
    id: 'audits',
    label: 'Audits',
    subtitle: 'Manage your audit processes and compliance.',
    brief: {
      objective: 'Configure your audit structure in Kepler.',
      whatWeNeed: ['Code', 'Description', 'Owner Email', 'Type'],
      outcome: 'Audits are live in Kepler.',
    },
    schema: [
      { label: 'Profit/Cost Centre', dot: '#15803d' },
    ],
    fields: [
      { id: 'code',        label: 'Code',        color: '#15803d', kw: ['code','centre code','cost code','profit code','id'], dbTable: 'master_profitcostcenter', dbColumn: 'code' },
      { id: 'description', label: 'Description', color: '#22c55e', kw: ['description','desc','name','details'], dbTable: 'master_profitcostcenter', dbColumn: 'description' },
      { id: 'owner_email', label: 'Owner Email', color: '#15803d', kw: ['owner email','owner','email','owner e-mail'], optional: false },
      { id: 'type',        label: 'Type',        color: '#22c55e', kw: ['type','centre type','cost centre','profit centre','classification'], optional: false },
    ],
    steps: ['Upload Data', 'Map Fields', 'Review', 'Activated'],
    sumLabels: ['Centres'],
    sumColors: ['#15803d'],
    successTitle: 'Profit-Cost Centres activated!',
    successMsg: (c) => `${c[0]} profit/cost centres are now live in Kepler.`,
  },
};

export const MODULE_IDS = Object.keys(MODULE_CONFIG);
